import type { Row, Table } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import { useTheme } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { PortfolioReport, SubTradeRequest } from '../types';
import type {
  AccountBreakdownRow,
  EnrichedHolding,
  GeneralInfoRow,
} from '../utils/portfolioRevampBuilders';
import { usePendingTradesForReport } from './usePendingTradesForReport';
import { usePortfolioRevampState } from './usePortfolioRevampState';
export type UsePortfolioRowDetailResult = {
  t: TFunction;
  theme: Theme;
  hasAccountBreakdown: boolean;
  hasPositions: boolean;
  headerProps: { report: PortfolioReport; theme: Theme };
  generalInfoProps: { generalInfoRows: GeneralInfoRow[]; theme: Theme };
  accountBreakdownProps: {
    accountBreakdownRows: AccountBreakdownRow[];
    theme: Theme;
  };
  pendingTradesProps: {
    pendingTrades: SubTradeRequest[];
    pendingTradesLoading: boolean;
    pendingTradesCalled: boolean;
    report: PortfolioReport;
    theme: Theme;
  };
  positionsProps: {
    report: PortfolioReport;
    holdingTable: Table<EnrichedHolding>;
    pagedPositionRows: Row<EnrichedHolding>[];
    positionsPage: number;
    setPositionsPage: (page: number) => void;
    positionsTotalPages: number;
    theme: Theme;
  };
};

export function usePortfolioRowDetail(
  report: PortfolioReport,
): UsePortfolioRowDetailResult {
  const { t } = useTranslation('portfolioTradingHub');
  const theme = useTheme();

  const pendingTradesState = usePendingTradesForReport(report);
  const { pendingTrades, pendingTradesCalled, pendingTradesLoading } =
    pendingTradesState;

  const {
    generalInfoRows,
    accountBreakdownRows,
    holdingTable,
    pagedPositionRows,
    positionsPage,
    setPositionsPage,
    positionsTotalPages,
  } = usePortfolioRevampState(report, pendingTradesState, t);

  const hasAccountBreakdown = accountBreakdownRows.length > 0;
  const hasPositions = (report.holdings?.length ?? 0) > 0;

  return {
    t,
    theme,
    hasAccountBreakdown,
    hasPositions,
    headerProps: { report, theme },
    generalInfoProps: { generalInfoRows, theme },
    accountBreakdownProps: { accountBreakdownRows, theme },
    pendingTradesProps: {
      pendingTrades,
      pendingTradesLoading,
      pendingTradesCalled,
      report,
      theme,
    },
    positionsProps: {
      report,
      holdingTable,
      pagedPositionRows,
      positionsPage,
      setPositionsPage,
      positionsTotalPages,
      theme,
    },
  };
}
